import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { appAgentService } from '../../../services/central-api/app-agent';
import { NavigationProp, useIsFocused, useNavigation } from '@react-navigation/native';
import { ActivityIndicator, FlatList, SafeAreaView, View } from 'react-native';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { UserNotInUnit } from '../../../services/central-api/base-service';
import ActivityAndServiceCard from './ActivityAndServiceCard';
import FloatingButton from '../../../components/FloatingButton';
import { AppAgentParamList } from '../../../typings/Params';

export default function ActivitiesAndServices() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();
    const navigation = useNavigation<NavigationProp<AppAgentParamList>>();

    const [userNotInUnit, setUserNotInUnit] = useState<boolean>(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getActivitiesAndServices'],
        queryFn: () => appAgentService.getActivitiesAndServices(),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser && isFocused;
    const isLoading = isEnabled && queryResult.isFetching;
    const { data, error } = queryResult;

    useEffect(() => {
        setUserNotInUnit(error instanceof UserNotInUnit);
    }, [error]);

    return (
        <SafeAreaView style={{ flex: 1 }}>
            {isLoading
                ? <View style={{ flexDirection: 'column', flex: 1, justifyContent: 'center', alignItems: 'center' }}>
                    <ActivityIndicator size='large' color={theme.colors.iconColor} />
                </View>
                : <>
                    <FlatList
                        data={data}
                        keyExtractor={(item) => String(item.id)}
                        contentContainerStyle={[styles.list, data?.length ? {} : { flex: 1 }]}
                        renderItem={({ item }) => (
                            <ActivityAndServiceCard
                                activity={item}
                                onEdit={() => {
                                    navigation.navigate('ActivitiesAndServicesDetails', {
                                        activityId: item.id,
                                    });
                                }}
                            />
                        )}
                        showsVerticalScrollIndicator={false}
                        ItemSeparatorComponent={() => <View style={{ height: 10 }} />}
                        ListEmptyComponent={
                            <View style={styles.emptyList}>
                                {userNotInUnit
                                    ? <MyAppText style={styles.emptyTitle}>{translate('joinUnitToUseThisScreen')}</MyAppText>
                                    : <>
                                        <MyAppText style={styles.emptyTitle}>{translate('noActivityRegistered')}</MyAppText>
                                        <MyAppText style={styles.emptyText}>{translate('useTheButtonToAddTheFirstOne')}</MyAppText>
                                    </>
                                }
                            </View>
                        }
                    />
                </>}
            <FloatingButton
                visible={!userNotInUnit && !isLoading}
                overrideWithAction
                offset={{ horizontal: 14, vertical: 32 }}
                actions={[
                    {
                        name: 'createActivity',
                        icon: faPlus,
                        onPress: () => {
                            navigation.navigate('ActivitiesAndServicesDetails', {});
                        }
                    }
                ]}
            />
        </SafeAreaView>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    list: {
        paddingTop: 16,
        paddingBottom: 96,
    },
    emptyList: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center'
    },
    fab: {
        position: 'absolute',
        right: 20,
        bottom: 24,
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primaryButton.background,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#000',
        shadowOpacity: 0.2,
        shadowRadius: 8,
        shadowOffset: { width: 0, height: 4 },
        elevation: 4,
    },
    emptyTitle: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        textAlign: 'center'
    },
    emptyText: {
        fontSize: 16,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
        textAlign: 'center'
    },
}));
